import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO'; // Import the SEO component
import { productsByCategory } from './productsData';

function OurProducts() {
    const [activeFilter, setActiveFilter] = React.useState('All');

    // SEO Structured Data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Galle Ceylon Spices Products",
        "description": "Explore our premium collection of Ceylon cinnamon, whole spices, and spice powders from Galle Ceylon Spices International.",
        "url": "https://www.galleceylonspices.com/shop",
        "itemListElement": productsByCategory[activeFilter].map((product, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "Product",
                "name": product.name,
                "image": product.image,
                "url": `https://www.galleceylonspices.com/product/${product.id}`,
                "description": `Premium ${product.name} from Galle Ceylon Spices International`
            }
        })),
        "publisher": {
            "@type": "Organization",
            "name": "Galle Ceylon Spices International (Pvt) Ltd",
            "logo": {
                "@type": "ImageObject",
                "url": "%PUBLIC_URL%/logo.png"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "448/2, Nawadagala",
                "addressLocality": "Elpitiya",
                "postalCode": "80000",
                "addressCountry": "Sri Lanka"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+94-77-929-1829",
                "contactType": "Customer Service",
                "contactOption": "WhatsApp",
                "areaServed": "Worldwide",
                "availableLanguage": "English"
            }
        }
    };

    return (
        <div className="container my-4 py-4">
            <SEO
                title="Shop Our Products - Galle Ceylon Spices International"
                description="Discover premium Ceylon cinnamon, whole spices, and spice powders from Galle Ceylon Spices International. Browse our collection and add to your quote request."
                keywords="Ceylon cinnamon, whole spices, spice powders, Galle Ceylon Spices, Sri Lankan spices, cinnamon products"
                canonicalUrl="https://www.galleceylonspices.com/shop"
                ogImage="%PUBLIC_URL%/logo.png"
                structuredData={structuredData}
            />
            {/* Products Section */}
            <section
                className="mt-4 py-4 rounded-3 shadow-lg animate__animated animate__fadeIn"
                style={{
                    animationDuration: '1s',
                    backgroundColor: 'rgba(193, 154, 107, 0.1)',
                }}
            >
                <h2
                    className="text-center mb-4 position-relative"
                    style={{
                        fontSize: 'clamp(2rem, 5vw, 2.8rem)',
                        letterSpacing: '0.5px',
                        color: '#355E3B',
                        fontFamily: 'Georgia',
                        lineHeight: '1.2',
                    }}
                >
                    Galle Ceylon Spices Products
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '-8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '80px',
                            height: '2px',
                            backgroundColor: '#C19A6B',
                            borderRadius: '2px',
                        }}
                    />
                </h2>

                {/* Filter Buttons */}
                <div className="d-flex justify-content-center gap-2 mb-4 flex-wrap" role="tablist" aria-label="Product category filters">
                    {['All', 'Cinnamon Products', 'Whole Spices', 'Spice Powders'].map((filter) => (
                        <button
                            key={filter}
                            className={`btn rounded-pill px-3 py-2 shadow-sm product-filter ${activeFilter === filter ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeFilter === filter}
                            aria-label={`Filter by ${filter}`}
                            onClick={() => setActiveFilter(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                {/* Product Cards */}
                {productsByCategory[activeFilter].length > 0 ? (
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                        {productsByCategory[activeFilter].map((product, index) => (
                            <div key={product.id} className="col">
                                <div
                                    className="card h-100 shadow-sm animate__animated animate__fadeInUp product-card"
                                    style={{
                                        animationDelay: `${index * 0.1}s`,
                                        animationDuration: '0.8s',
                                        border: '1px solid rgba(193, 154, 107, 0.3)',
                                        borderRadius: '8px',
                                        backgroundColor: '#FFFFFF',
                                    }}
                                >
                                    <div style={{ padding: '10px' }}>
                                        <img
                                            src={product.image}
                                            className="card-img-top"
                                            alt={product.name}
                                            style={{
                                                height: 'clamp(150px, 30vw, 250px)',
                                                width: '100%',
                                                objectFit: 'contain',
                                            }}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="card-body d-flex flex-column justify-content-between p-3">
                                        <h5
                                            className="card-title text-center"
                                            style={{
                                                fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                                                fontFamily: 'Georgia',
                                                color: '#355E3B',
                                                fontWeight: 'bold',
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            {product.name}
                                        </h5>
                                        <Link
                                            to={`/product/${product.id}`}
                                            className="btn w-100 product-link"
                                            aria-label={`Read more about ${product.name}`}
                                            style={{
                                                backgroundColor: '#C19A6B',
                                                borderColor: '#C19A6B',
                                                color: '#FFFFFF',
                                                fontFamily: 'Georgia',
                                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                                padding: 'clamp(6px, 1.5vw, 10px) clamp(12px, 3vw, 20px)',
                                            }}
                                        >
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-muted" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', padding: '2rem' }}>
                        No products available in this category.
                    </div>
                )}
            </section>

            {/* Inline CSS for styles and responsiveness */}
            <style>
                {`
                    .product-filter {
                        background-color: transparent;
                        color: #C19A6B;
                        border: 2px solid #C19A6B;
                        font-family: Georgia;
                        font-size: clamp(0.9rem, 2vw, 1.1rem);
                        transition: all 0.3s ease-in-out;
                    }
                    .product-filter:hover:not(.active), .product-filter:focus:not(.active) {
                        background-color: #C19A6B;
                        color: #FFFFFF;
                        transform: scale(1.05);
                    }
                    .product-filter.active {
                        background-color: #C19A6B;
                        color: #FFFFFF;
                    }
                    .product-card {
                        transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
                    }
                    .product-card:hover {
                        transform: scale(1.03);
                        box-shadow: 0 6px 15px rgba(193, 154, 107, 0.3);
                    }
                    .product-card img {
                        transition: opacity 0.3s ease-in-out;
                    }
                    .product-card img:hover {
                        opacity: 0.95;
                    }
                    .product-link {
                        transition: all 0.3s ease-in-out;
                        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
                    }
                    .product-link:hover, .product-link:focus {
                        background-color: #A67B4D !important;
                        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
                    }
                    @media (max-width: 576px) {
                        .container.my-4 {
                            margin-top: 1rem !important;
                            margin-bottom: 1rem !important;
                        }
                        .container.py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .mt-4 {
                            margin-top: 1rem !important;
                        }
                        .py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1rem !important;
                        }
                        .card-img-top {
                            height: 150px !important;
                        }
                        .card-body {
                            padding: 0.75rem !important;
                        }
                        .btn {
                            padding: 6px 12px !important;
                            font-size: 0.9rem !important;
                        }
                    }
                    @media (max-width: 576px) and (orientation: landscape) {
                        .card-img-top {
                            height: 120px !important;
                        }
                        .card-body {
                            padding: 0.5rem !important;
                        }
                        .btn {
                            padding: 4px 10px !important;
                            font-size: 0.8rem !important;
                        }
                    }
                    @media (min-width: 577px) and (max-width: 768px) {
                        .container.my-4 {
                            margin-top: 2rem !important;
                            margin-bottom: 2rem !important;
                        }
                        .container.py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .mt-4 {
                            margin-top: 1.5rem !important;
                        }
                        .py-4 {
                            padding-top: 1.5rem !important;
                            padding-bottom: 1.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1.5rem !important;
                        }
                        .card-img-top {
                            height: 200px !important;
                        }
                    }
                    @media (min-width: 769px) and (max-width: 1024px) {
                        .container.my-4 {
                            margin-top: 3rem !important;
                            margin-bottom: 3rem !important;
                        }
                        .container.py-4 {
                            padding-top: 3rem !important;
                            padding-bottom: 3rem !important;
                        }
                        .mt-4 {
                            margin-top: 2rem !important;
                        }
                        .py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2rem !important;
                        }
                        .card-img-top {
                            height: 220px !important;
                        }
                    }
                    @media (min-width: 1025px) {
                        .container.my-4 {
                            margin-top: 4rem !important;
                            margin-bottom: 4rem !important;
                        }
                        .container.py-4 {
                            padding-top: 4rem !important;
                            padding-bottom: 4rem !important;
                        }
                        .mt-4 {
                            margin-top: 2.5rem !important;
                        }
                        .py-4 {
                            padding-top: 2.5rem !important;
                            padding-bottom: 2.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2.5rem !important;
                        }
                        .card-img-top {
                            height: 250px !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}

export default OurProducts;